/**
 * Mapping of request header names to their keys in the additional_attributes
 * object of the shopping_finished Segment event.
 *
 * Key:   exact request header name (lowercase)
 * Value: the key used in the Segment payload
 *
 * To add a new header, append an entry here. The header will automatically
 * be included in additional_attributes for all shopping_finished events.
 *
 * Do NOT add headers containing sensitive data — identity tokens, auth
 * credentials, precise geolocation, or internal infrastructure tokens.
 */
export const HEADERS_FOR_ADDITIONAL_ATTRIBUTES: Record<string, string> = {
  'cf-ray': 'cf_ray',
  'x-cf-bot-score': 'x_cf_bot_score',
  'x-cf-verified-bot': 'x_cf_verified_bot',
};

/**
 * Allowlist of redirect query params copied onto additional_attributes of the
 * shopping_finished Segment event.
 *
 * Key:   exact query param name
 * Value: the Segment payload key and the exact values it accepts
 *
 * Values match ignoring case and surrounding whitespace, and the event carries
 * the allowed value exactly as written here, so downstream joins see one
 * canonical string. Any other value, and any param not listed here, is left
 * out of additional_attributes. This shapes additional_attributes only: the
 * full query string still reaches the event through redirect_url.
 *
 * Only add enumerated fields whose use on this event analytics has approved.
 */
export const QUERY_PARAMS_FOR_ADDITIONAL_ATTRIBUTES: Record<
  string,
  { key: string; allowedValues: readonly string[] }
> = {
  // Paid Approval Odds Banner on Paid Roundup LP instrumentation spec.
  approval_odds: {
    key: 'approval_odds',
    allowedValues: ['EXCELLENT_AO', 'GOOD_AO', 'FAIR_AO', 'POOR_AO'],
  },
};

/**
 * Returns the allowed value matching `value` ignoring case and surrounding
 * whitespace, or undefined when nothing matches. The allowed value is
 * returned as written above, so the event always carries the canonical
 * string.
 */
export function matchAllowedValue(
  value: string,
  allowedValues: readonly string[]
): string | undefined {
  const normalized = value.trim().toUpperCase();
  return allowedValues.find(
    (allowedValue) => allowedValue.toUpperCase() === normalized
  );
}
